import React, { useEffect, useState } from "react";
import getIcon from "../../utils/Icon";

export default function CoinBadge({ isCrystal,color, count, name, isGiveaway, icon, token, isCenter, bg, iconWidth, iconHeight, isHeader, selectedBlockchain, headerToken, countOnly, fixedWidth, customColorText, customTextClass, customIconClass, customCurrencyClass, classicCount, fullCount }) {
    const [dynamicGradient, setDynamicGradient] = useState("");

    const blockChainsList = [
        { key: 'ETH', name: 'ETH', fullName: 'Ethereum' },
        { key: 'BNB', name: 'BSC', fullName: 'Binance' },
        { key: 'solana', name: 'SOL', fullName: 'Solana' },
        { key: 'sui', name: 'SUI', fullName: 'Sui' },
        { key: 'base', name: 'BASE', fullName: 'Base' },
        { key: 'TON', name: 'TON', fullName: 'TON' },
        { key: 'polygon_chain', name: 'MATIC', fullName: 'Polygon' },
        { key: 'optimism', name: 'OP', fullName: 'Optimism' },
        { key: 'arbitrum', name: 'ARB', fullName: 'Arbitrum' },
        { key: 'avalanche', name: 'AVAX', fullName: 'Avalanche' },
        // Hardhat здесь НЕ прячем за флагом: это справочник для отображения бейджа,
        // а не выбор сети. Убрать — у ранее созданных тестовых промо пропадёт подпись.
        // Создание промо в тестовой сети закрыто в формах (см. utils/featureFlags.js).
        { key: 'hardhat', name: 'HH', fullName: 'Hardhat' }
    ]

    const toNumericCount = (value) => {
        if (typeof value === 'number') return value;
        if (typeof value === 'string' && value.trim() !== '') {
            const parsed = Number(value);
            if (!Number.isNaN(parsed)) return parsed;
        }
        return null;
    };

    // Классик: до 1000 как есть, дальше 1.01k / 1.01M / 1.01B
    const formatClassicCount = (num) => {
        if (num < 1000) {
            if (Number.isInteger(num)) return String(num);
            return num.toFixed(4).replace(/\.?0+$/, '');
        }
        if (num >= 1000000000000) {
            return (num / 1000000000000).toFixed(2) + 'T';
        }
        if (num >= 1000000000) {
            return (num / 1000000000).toFixed(2) + 'B';
        }
        if (num >= 1000000) {
            return (num / 1000000).toFixed(2) + 'M';
        }
        return (num / 1000).toFixed(2) + 'K';
    };

    // Полный: без сокращений
    const formatFullCount = (num) => {
        if (Number.isInteger(num)) return String(num);
        return String(num);
    };

    // Старое форматирование (обратная совместимость)
    const formatCount = (num) => {
        if (num >= 1000000000000) {
            return (num / 1000000000000).toFixed(2).replace(/\.?0+$/, '') + 'T';
        } else if (num >= 1000000000) {
            return (num / 1000000000).toFixed(2).replace(/\.?0+$/, '') + 'B';
        } else if (num >= 1000000) {
            return (num / 1000000).toFixed(2).replace(/\.?0+$/, '') + 'M';
        } else if (num >= 10000) {
            return (num / 1000).toFixed(2) + 'K';
        }
        return num.toLocaleString('en-US').replace(/,/g, ' ');
    };

    const formatCurrencyName = (value) => {
        const str = String(value || '');
        if (str.length <= 6) return str;
        return `${str.slice(0, 5)}...`;
    };

    const getDisplayCount = () => {
        if (fullCount) {
            if (typeof count === 'string') return count;
            const numeric = toNumericCount(count);
            if (numeric === null) return count;
            return formatFullCount(numeric);
        }

        const numeric = toNumericCount(count);
        if (numeric === null) return count;

        if (classicCount) return formatClassicCount(numeric);
        return formatCount(numeric);
    };

    useEffect(() => {
        if (!icon && !token) {
            setDynamicGradient("");
            return;
        }

        const img = new Image();
        img.crossOrigin = "anonymous";
        
        // Если указан токен, используем его напрямую, иначе используем getIcon
        if (token) {
            img.src = token;
        } else {
            img.src = getIcon(icon);
        }

        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0, img.width, img.height);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const pixels = imageData.data;

            const colorMap = {};
            for (let i = 0; i < pixels.length; i += 4) {
                const r = pixels[i];
                const g = pixels[i + 1];
                const b = pixels[i + 2];

                const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

                if (brightness > 30 && brightness < 230) {
                    const color = `rgb(${r},${g},${b})`;
                    colorMap[color] = (colorMap[color] || 0) + 1;
                }
            }

            const sortedColors = Object.entries(colorMap)
                .sort((a, b) => b[1] - a[1])
                .map(([color]) => color);

            const startColor = sortedColors[0] || "rgb(234, 234, 234)";
            const midColor = sortedColors[1] || startColor;
            const endColor = sortedColors[2] || midColor;

            setDynamicGradient(`linear-gradient(to right, ${startColor}, ${midColor}, ${endColor})`);
        };
    }, [icon, token]);

    const hasGradientSource = icon || token;
    const showIcon = hasGradientSource && !countOnly;
    const headerBadgeSrc = headerToken || (selectedBlockchain ? getIcon(selectedBlockchain) : null);
    const showHeaderBadge = isHeader && !countOnly && headerBadgeSrc;
    const resolvedWidth = fixedWidth != null
        ? (typeof fixedWidth === 'number' ? `${fixedWidth}px` : fixedWidth)
        : undefined;
    const textColorStyle = customColorText ? { color: customColorText } : undefined;
    const textColorClass = customColorText ? '' : 'text-white';

    return (
        <div
            className={`relative h-[30px] rounded-[16px] p-[2px] ${
                hasGradientSource ? "" : color 
            }`}
            style={{
                background: hasGradientSource
                    ? dynamicGradient || "linear-gradient(to right, #e66465, #9198e5)"
                    : undefined,
                width: resolvedWidth,
                minWidth: resolvedWidth,
                maxWidth: resolvedWidth,
            }}
        >
            <div
                className={`w-full h-full ${isHeader && !countOnly ? `pl-2` : `px-2`} ${bg ? "bg-" + bg : "bg-main"} rounded-2xl flex flex-row items-center ${
                    isCenter || countOnly ? "justify-center" : "justify-end gap-1 xs:gap-1"
                } ${!isGiveaway && !countOnly && "gap-1"} border border-transparent min-w-0`}
            >
                <span
                    className={`${textColorClass}  text-[12px] font-semi font-sans flex-shrink-0 ${customTextClass || ''}`}
                    style={textColorStyle}
                >
                    {getDisplayCount()}
                </span>
                {showIcon && (
                    <img
                        src={token || getIcon(icon)}
                        className={`icon-img flex-shrink-0 ${iconWidth ||iconHeight && 'xs:w-[25px] xs:h-[25px]'} ${token&& 'rounded-full'} ${customIconClass || ''}`}
                        alt="icon"
                        style={
                            iconWidth || iconHeight
                                ? {
                                    width: iconWidth ? iconWidth : undefined,
                                    height: iconHeight ? iconHeight : undefined,
                                    minWidth: iconWidth ? iconWidth : undefined,
                                    minHeight: iconHeight ? iconHeight : undefined,
                                }
                                : { width: 10, height: 10 }
                        }
                    />
                )}
                {
                    isCrystal && (
                        <span
                            className={`${textColorClass}  text-[12px] font-semibold font-sans flex-shrink-0 ${customTextClass || ''}`}
                            style={textColorStyle}
                        >
                            Crystals
                        </span>
                    )
                }
                {name && !countOnly && (
                    <span
                        className={`${textColorClass} w-10 flex-shrink-0 whitespace-nowrap text-[12px] font-semi font-sans  min-w-0 max-w-[60px] xs:max-w-[40px] ${customCurrencyClass || ''}`}
                        style={textColorStyle}
                    >
                        {formatCurrencyName(name)}
                    </span>
                )}

                {showHeaderBadge && (
                        <div 
                            className="flex-shrink-0 w-[28px] h-[calc(100%+4px)] translate-x-[2px] translate-y-[-0px] p-[2px] flex items-center justify-center rounded-full whitespace-nowrap  "
                            style={{
                                background: (icon || token)
                                    ? dynamicGradient || "linear-gradient(to right, #e66465, #9198e5)"
                                    : undefined,
                            }}
                            >
                                <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
                                <img
                                    width={16}
                                    height={16}
                                    src={headerBadgeSrc}
                                    alt={blockChainsList.find(b => b.key === selectedBlockchain)?.name || 'Badge'}
                                    className={`flex-shrink-0 object-contain ${headerToken ? 'rounded-full' : ''}`}
                                />
                                </div>
                        </div>
                    )}
            </div>
        </div>
    );
}
